import React from "react"
import { css, type Theme, type SerializedStyles } from "@emotion/react"
import IconButton from "@myvp/shared/src/components/buttons/icon-button"
import Times from "@myvp/shared/src/icons/times"
import Info from "@myvp/shared/src/icons/info"
import Warning from "@myvp/shared/src/icons/warning"
import {
  Regular12UI,
  Regular14Paragraph,
  Semibold14,
} from "@myvp/shared/src/typography"
import styled from "@emotion/styled"
import Button, {
  ExternalButtonProps,
} from "@myvp/shared/src/components/buttons/button"
import { heightStylesPx } from "@myvp/shared/src/components/buttons/button-utils"
import breakpoints from "@myvp/shared/src/styles/breakpoints"
import Alert from "@myvp/shared/src/icons/alert"
import Success from "@myvp/shared/src/icons/success"

export type BannerSize = "extraSmall" | "small" | "large"

const sizePaddingMap = {
  extraSmall: css`
    padding: 8px;
  `,
  small: css`
    padding: 8px 16px;
  `,
  large: css`
    padding: 16px;
  `,
}

const bannerCss = (size: BannerSize) => [
  css`
    display: flex;
    column-gap: ${size === "extraSmall" ? "4px" : "16px"};
    border: ${size === "extraSmall" ? "1px solid transparent" : "none"};
    border-radius: 4px;
    align-items: flex-start;
  `,
  sizePaddingMap[size],
]

const iconSizeMap: Record<BannerSize, "small" | "medium"> = {
  extraSmall: "small",
  small: "medium",
  large: "medium",
}
const iconVariantMap: Record<BannerSize, "default" | "filled"> = {
  extraSmall: "default",
  small: "filled",
  large: "filled",
}

export const variant = {
  warning: {
    icon: (size: BannerSize) => (
      <Warning
        size={iconSizeMap[size]}
        variant={size === "extraSmall" ? "outlined" : "filled"}
      />
    ),
    container: (theme: Theme, size: BannerSize) => css`
      background: ${theme.palette.surface.warning.default};
      border-color: ${size === "extraSmall"
        ? theme.palette.content.warning.strong
        : "transparent"};
    `,
  },
  error: {
    icon: (size: BannerSize) => (
      <Alert
        variant={size === "extraSmall" ? "outlined" : "filled"}
        size={iconSizeMap[size]}
      />
    ),
    container: (theme: Theme, size: BannerSize) => css`
      background: ${theme.palette.surface.critical.default};
      border-color: ${size === "extraSmall"
        ? theme.palette.surface.critical.default
        : "transparent"};
    `,
  },
  success: {
    icon: (size: BannerSize) => (
      <Success variant="filled" size={iconSizeMap[size]} />
    ),
    container: (theme: Theme, size: BannerSize) => css`
      background: ${theme.palette.surface.success.default};
      border-color: ${size === "extraSmall"
        ? theme.palette.surface.success.strong
        : "transparent"};
    `,
  },
  info: {
    icon: (size: BannerSize) => (
      <Info
        size={iconSizeMap[size]}
        color="link"
        variant={iconVariantMap[size]}
      />
    ),
    container: (theme: Theme, size: BannerSize) => css`
      background: ${theme.palette.surface.info.default};
      border-color: ${size === "extraSmall"
        ? theme.palette.surface.info.strong
        : "transparent"};
    `,
  },
  neutral: {
    icon: (size: BannerSize) => (
      <Info size={iconSizeMap[size]} variant={iconVariantMap[size]} />
    ),
    container: (theme: Theme, size: BannerSize) => css`
      background: ${theme.palette.surface.neutral.alt};
      border-color: ${size === "extraSmall"
        ? theme.palette.border.neutral.subtle
        : "transparent"};
    `,
  },
  brand: {
    icon: (size: BannerSize) => (
      <Info
        size={iconSizeMap[size]}
        variant={iconVariantMap[size]}
        color="highlight"
      />
    ),
    container: (theme: Theme, size: BannerSize) => css`
      background: ${theme.palette.surface.secondary.default};
      border-color: ${size === "extraSmall"
        ? theme.palette.surface.secondary.disabled
        : "transparent"};
    `,
  },
} as const

const messageCss = (theme: Theme) => css`
  display: flex;
  gap: 16px;
  flex-grow: 1;
  > div {
    max-width: 800px;
  }
  color: ${theme.palette.content.neutral.default};
  overflow-wrap: anywhere;
`
const dismissButtonCss = css`
  width: 24px;
  min-width: 24px;
  height: 24px;
  min-height: 24px;
`

export const BannerLinkTypography = Semibold14

type ButtonProps = Omit<
  ExternalButtonProps,
  "variant" | "height" | "className"
> & {
  css?: SerializedStyles
  "data-automationid"?: string
}

interface CommonBannerProps {
  className?: string
  variant?: keyof typeof variant
  displayIcon?: boolean
  "data-testid"?: string
  ref?: React.RefObject<HTMLDivElement>
  message?: React.ReactNode
}

interface ExtraSmallBannerProps extends CommonBannerProps {
  size: "extraSmall"
  message: React.ReactNode
  title?: never
  titleTypography?: never
  messageTypography?: never
  subMessage?: never
  subMessageTypography?: never
  overrideIcon?: never
  primaryAction?: never
  secondaryAction?: never
  dismissButton?: never
}

interface StandardBannerProps extends CommonBannerProps {
  size?: "small" | "large"
  title?: React.ReactNode
  titleTypography?: typeof Semibold14
  messageTypography?: typeof Regular14Paragraph
  subMessage?: React.ReactNode
  subMessageTypography?: typeof Regular14Paragraph
  overrideIcon?: React.ReactNode
}

type BannerActions =
  | {
      dismissButton?: { title: string; onClick: () => void }
      secondaryAction?: never
      primaryAction?: never
    }
  | {
      dismissButton?: never
      secondaryAction?: ButtonProps
      primaryAction?: ButtonProps | null
    }

export type BannerProps =
  | ExtraSmallBannerProps
  | (StandardBannerProps & BannerActions)

const Banner = (props: BannerProps) => {
  const displayIcon = props.displayIcon ?? true
  const variantCss = variant[props.variant ?? "neutral"]
  const size = props.size ?? (props.title ? "large" : "small")
  const containerStyles = [
    bannerCss(size),
    (theme: Theme) => variantCss.container(theme, size),
  ]

  if (props.size === "extraSmall") {
    return (
      <div
        className={props.className}
        ref={props.ref}
        data-testid={props["data-testid"]}
        css={containerStyles}
      >
        {displayIcon && variantCss.icon(size)}
        <Regular12UI
          as="div"
          css={(theme) => css`
            color: ${theme.palette.content.neutral.default};
          `}
        >
          {props.message}
        </Regular12UI>
      </div>
    )
  }
  if (!props.title && !props.message) {
    return null
  }

  const Title = props.titleTypography ?? Semibold14
  const Message = props.messageTypography ?? Regular14Paragraph
  const SubMessage = props.subMessageTypography ?? Regular14Paragraph

  const containsSecondaryOrPrimaryAction =
    (("secondaryAction" in props && props.secondaryAction) ||
      ("primaryAction" in props && props.primaryAction)) &&
    !props.dismissButton

  return (
    <div
      className={props.className}
      ref={props.ref}
      css={[
        ...containerStyles,
        containsSecondaryOrPrimaryAction
          ? css`
              ${breakpoints("sm")} {
                flex-direction: column;
                gap: 16px;
              }
            `
          : null,
      ]}
      data-testid={props["data-testid"]}
    >
      <div css={messageCss}>
        {displayIcon && (props.overrideIcon ?? variantCss.icon(size))}
        <div>
          {!!props.title && <Title>{props.title}</Title>}
          {!!props.message && <Message as="div">{props.message}</Message>}
          {!!props.subMessage && (
            <SubMessage
              as="div"
              css={(theme) => css`
                color: ${theme.palette.content.neutral.subtle};
              `}
            >
              {props.subMessage}
            </SubMessage>
          )}
        </div>
      </div>
      {(() => {
        if ("dismissButton" in props && props.dismissButton) {
          return (
            <IconButton
              css={dismissButtonCss}
              variant="tertiary"
              height="small"
              internalType="button"
              icon={() => <Times size="medium" />}
              title={props.dismissButton.title}
              onClick={props.dismissButton.onClick}
            />
          )
        }
        if (containsSecondaryOrPrimaryAction) {
          return (
            <div
              css={[
                css`
                  display: flex;
                  gap: 16px;
                  height: ${heightStylesPx.medium}px;
                  justify-content: flex-end;
                `,
              ]}
            >
              {props.secondaryAction && (
                <Button
                  {...props.secondaryAction}
                  variant="secondary"
                  type="button"
                  height="medium"
                />
              )}
              {props.primaryAction && props.secondaryAction && (
                <div
                  css={(theme) => css`
                    width: 1px;
                    background: ${theme.palette.border.neutral.default};
                  `}
                />
              )}
              {props.primaryAction && (
                <Button
                  {...props.primaryAction}
                  variant="primary"
                  type="button"
                  height="medium"
                />
              )}
            </div>
          )
        }
      })()}
    </div>
  )
}

export default styled(Banner)``
